// @flow

const CUSTOMERIO_API_KEY = process.env.REACT_APP_CUSTOMERIO_API_KEY;
const CUSTOMERIO_ANONYMOUS_ID_KEY = 'ajs_anonymous_id';
let lastTrackedPath = null;

/**
 * Retrieves the anonymous ID from the cookie.
 *
 * @returns {string|undefined} The anonymous ID if found, otherwise undefined.
 *
 * @description
 * This function attempts to retrieve the anonymous ID from the cookie set by Customer.io.
 * It parses the document's cookies and looks for the specific key defined by
 * `CUSTOMERIO_ANONYMOUS_ID_KEY`. If the key is found, it returns the corresponding value.
 * If the key is not found, it returns undefined.
 *
 * Note: This function is used to maintain tracking continuity when a user is identified
 * with an email address after initially being tracked anonymously. The anonymous ID is
 * important for linking the anonymous user session with the identified user session.
 * This is particularly useful for analytics and user behavior tracking.
 * The function uses the `document.cookie` property to access the cookies stored in the
 * browser. It splits the cookie string to find the specific key and retrieves its value.
 * If the key is not found, it returns undefined.
 * This function is called during the initialization of the Customer.io analytics
 * integration to ensure that the anonymous ID is set before identifying the user with
 * their email address.
 */
const getAnonymousId = () => {
  // get from cookie
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${CUSTOMERIO_ANONYMOUS_ID_KEY}=`);
  if (parts.length === 2) {
    return parts.pop().split(';').shift();
  }
};

/**
 * Initializes the Customer.io analytics integration.
 *
 * @param {Object} tenantApp - The tenant application object containing external information.
 * @param {Object} tenantApp.externalInfo - External information about the tenant.
 * @param {string} [tenantApp.externalInfo.email] - The email address of the user, if available.
 *
 * @description
 * This function injects the Customer.io analytics script into the page and initializes it
 * with the provided API key. It also attempts to identify the user using an anonymous ID
 * and, if available, their email address. The function includes retry logic to ensure
 * the analytics object is ready before identifying the user.
 *
 * Retry Logic:
 * - The function retries up to 10 times, with a 200ms interval between attempts, to check
 *   if the analytics object (`window.cioanalytics`) is ready.
 */
export const initCustomerIO = tenantApp => {
  // Only init at first load when data is ready
  // the window.cioanalytics is included in the index.html
  if (CUSTOMERIO_API_KEY && tenantApp && window.cioanalytics) {
    const email = tenantApp?.externalInfo?.email;

    // The script will initialize immediately, but we want to make sure the analytics object is ready
    const identifyUser = () => {
      const anonymousId = getAnonymousId();

      try {
        // console.log('CustomerIO.identifyUser() anonymousId', anonymousId);
        // Use setAnonymousId first to ensure tracking continuity
        window.cioanalytics.setAnonymousId(anonymousId);

        // If we have email, also identify the user
        if (email) {
          window.cioanalytics.identify(email, { anonymous_id: anonymousId });
        }
      } catch (error) {
        console.error('Error identifying user with Customer.io:', error);
      }
    };

    // Start checking for the analytics object
    identifyUser();
  }
};

export const trackCustomerIOPage = (pagePath, pageReferrerPath = '') => {
  if (!CUSTOMERIO_API_KEY || !window.cioanalytics || !pagePath) {
    return;
  }

  if (lastTrackedPath === pagePath) {
    return;
  }

  lastTrackedPath = pagePath;

  const pagePayload = {
    path: pagePath,
    referrer_path: pageReferrerPath || '',
    url: `${window.location.origin}${pagePath}${window.location.search}`,
    referrer: document.referrer || ''
  };

  console.log('[CIO] track page', {
    path: pagePayload.path,
    referrer_path: pagePayload.referrer_path
  });

  try {
    window.cioanalytics.page(undefined, document.title || '', pagePayload);
  } catch (error) {
    console.error('Error tracking page with Customer.io:', error);
  }
};
